/**
 * MeleeAttackAction - behavior tree action for melee combat.
 */

import * as THREE from 'three';

import { CharacterStatsComponent } from '../../../../../components/CharacterStatsComponent.js';
import { NpcMovementComponent } from '../../../../../components/movement/NpcMovementComponent.js';
import { EngineClass } from '../../../../../systems/ClassRegistry.js';
import { BehaviorStatus } from '../../../BehaviorNode.js';
import { BehaviorAction, type BehaviorActionOptions } from '../../BehaviorAction.js';

import type { Actor } from '../../../../../actors/Actor.js';
import type { Blackboard } from '../../../blackboard/Blackboard.js';

export interface MeleeAttackActionOptions extends BehaviorActionOptions {
  targetActorKey?: string;
  attackRange?: number;
  damage?: number;
  attackCooldown?: number;
  attackDuration?: number;
}

@EngineClass('MeleeAttackAction')
export class MeleeAttackAction extends BehaviorAction {
  private targetActorKey?: string;
  private attackRange: number;
  private damage: number;
  private attackCooldown: number;
  private attackDuration: number;
  private movementComponent: NpcMovementComponent | null = null;
  private lastAttackTime: number = 0;
  private isAttacking: boolean = false;
  private attackStartTime: number = 0;

  constructor(options: MeleeAttackActionOptions = {}) {
    super({
      name: 'MeleeAttack',
      ...options
    });

    this.targetActorKey = options.targetActorKey ?? 'PlayerActor';
    this.attackRange = options.attackRange ?? 3.0;
    this.damage = options.damage ?? 10;
    this.attackCooldown = options.attackCooldown ?? 2.0;
    this.attackDuration = options.attackDuration ?? 0.5;
  }

  protected override onInitialize(blackboard: Blackboard): void {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return;
    }

    this.movementComponent = owner.getComponent(NpcMovementComponent);
    if (!this.movementComponent) {
      console.warn('MeleeAttackAction: No NpcMovementComponent found on actor');
    }
  }

  protected override onEnter(blackboard: Blackboard): void {
    this.isAttacking = false;
  }

  protected override async onUpdate(blackboard: Blackboard, deltaTime: number): Promise<BehaviorStatus> {
    const owner = this.getOwner(blackboard);
    if (!owner) {
      return BehaviorStatus.Failure;
    }

    // Get target
    const target = this.getTarget(blackboard);
    if (!target) {
      return BehaviorStatus.Failure;
    }

    // Get positions
    const ownerPosition = new THREE.Vector3();
    const targetPosition = new THREE.Vector3();
    owner.rootComponent.getWorldPosition(ownerPosition);
    target.rootComponent.getWorldPosition(targetPosition);

    // Calculate distance
    const distance = ownerPosition.distanceTo(targetPosition);

    // Check if target is in range
    if (distance > this.attackRange) {
      return BehaviorStatus.Failure;
    }

    const currentTime = Date.now() / 1000;

    // Check if we're currently attacking
    if (this.isAttacking) {
      const attackElapsed = currentTime - this.attackStartTime;
      if (attackElapsed >= this.attackDuration) {
        // Attack finished
        this.isAttacking = false;
        return BehaviorStatus.Success;
      }
      return BehaviorStatus.Running;
    }

    // Check cooldown
    if (currentTime - this.lastAttackTime < this.attackCooldown) {
      return BehaviorStatus.Running;
    }

    // Start attack
    this.startAttack(owner, target);
    this.lastAttackTime = currentTime;
    this.attackStartTime = currentTime;
    this.isAttacking = true;

    return BehaviorStatus.Running;
  }

  protected override onExit(blackboard: Blackboard, status: BehaviorStatus): void {
    this.isAttacking = false;
  }

  private getTarget(blackboard: Blackboard): Actor | null {
    if (this.targetActorKey) {
      const actor = blackboard.getValue<Actor>(this.targetActorKey);
      if (actor) {
        return actor;
      }
    }
    return null;
  }

  private startAttack(owner: Actor, target: Actor): void {
    // Stop movement during attack
    if (this.movementComponent) {
      this.movementComponent.stop();
    }

    // Deal damage to target
    const targetStats = target.getComponent(CharacterStatsComponent);
    if (targetStats) {
      targetStats.takeDamage(this.damage);
    }

    // TODO: Add attack effects, sounds, etc.
  }
}
